import { useState, useEffect, useRef, RefObject } from 'react'

interface UseScrollToHighlightProps<T> {
  items: T[]
  onScroll: () => void
  highlightDuration?: number
}

interface UseScrollToHighlightReturn<T> {
  highlightedKey: T | null
  setHighlightedKey: (key: T | null) => void
  ref: RefObject<HTMLDivElement | null>
}

export function useScrollToHighlight<T extends number | string>({
  items,
  onScroll,
  highlightDuration = 3000,
}: UseScrollToHighlightProps<T>): UseScrollToHighlightReturn<T> {
  const [highlightedKey, setHighlightedKeyState] = useState<T | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const setHighlightedKey = (key: T | null) => {
    setHighlightedKeyState(key)
  }

  const onScrollRef = useRef(onScroll)
  onScrollRef.current = onScroll

  useEffect(() => {
    if (highlightedKey) {
      timerRef.current = setTimeout(() => {
        setHighlightedKeyState(null)
      }, highlightDuration)
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current)
        }
      }
    }
  }, [highlightedKey, highlightDuration])

  useEffect(() => {
    if (highlightedKey) {
      const isInList = items.some((item) => item === highlightedKey)
      if (isInList && onScrollRef.current) {
        onScrollRef.current()
      }
    }
  }, [items, highlightedKey])

  return {
    highlightedKey,
    setHighlightedKey,
    ref: scrollRef,
  }
}
